import React, { useMemo, useState } from 'react';
import { Alert, Chip, Divider, Stack, TextField, Typography } from '@mui/material';
import type { LearnedAutoFillPatternV1 } from '../../../../shared/lib/quickFill/learnedAutoFillLibrary';
import { buildQuickFillKeyLibraryMatchDiagnostic } from '../../lib/quickFillKeyLibraryDiagnostics';
import {
  getKeyLibraryCompactFieldSx,
  getKeyLibraryMatchPanelSx,
} from './quickFillKeyLibraryStyles';

interface QuickFillKeyLibraryMatchTesterProps {
  entries: readonly LearnedAutoFillPatternV1[];
}

const displayValue = (value: string | undefined): string => value || '\u2014';

const QuickFillKeyLibraryMatchTester: React.FC<QuickFillKeyLibraryMatchTesterProps> = ({
  entries,
}) => {
  const [familyName, setFamilyName] = useState('');
  const diagnostic = useMemo(
    () => buildQuickFillKeyLibraryMatchDiagnostic(familyName, entries),
    [entries, familyName]
  );

  return (
    <Stack spacing={1.25} sx={getKeyLibraryMatchPanelSx}>
      <Stack spacing={0.25}>
        <Typography variant="subtitle1" sx={{ fontWeight: 750 }}>
          Test a family name
        </Typography>
        <Typography variant="body2" color="text.secondary">
          Preview the exact rule ordering used by Quick Fill Auto-fill.
        </Typography>
      </Stack>
      <Alert severity="info">
        Testing does not change Quick Fill rows or save the dictionary.
      </Alert>
      <TextField
        autoFocus
        fullWidth
        size="small"
        label="Family name to test"
        value={familyName}
        onChange={(event) => setFamilyName(event.target.value)}
        sx={(theme) => getKeyLibraryCompactFieldSx(theme)}
      />
      {diagnostic.source !== 'idle' && (
        <>
          <Stack direction="row" spacing={0.75} useFlexGap flexWrap="wrap">
            <Typography variant="subtitle2">Normalized keys:</Typography>
            {diagnostic.normalizedKeys.length > 0
              ? diagnostic.normalizedKeys.map((key) => <Chip key={key} size="small" label={key} />)
              : <Typography variant="body2">None</Typography>}
          </Stack>
          {diagnostic.builtInTakesPrecedence && (
            <Alert severity="warning">
              A PipeEX built-in wins before {diagnostic.learnedCandidates.length} matching local
              {diagnostic.learnedCandidates.length === 1 ? ' candidate' : ' candidates'}.
            </Alert>
          )}
          {diagnostic.hasAmbiguousLearnedMappings && (
            <Alert severity="warning">
              Equally specific local candidates produce different mappings. The newest entry wins,
              followed by stable ID order.
            </Alert>
          )}
          {diagnostic.source === 'none' ? (
            <Alert severity="warning">No built-in or local rule matches this family name.</Alert>
          ) : (
            <Stack spacing={0.5}>
              <Typography variant="subtitle2">
                Winning source: {diagnostic.source === 'built-in' ? 'PipeEX built-in' : 'Local key'}
              </Typography>
              <Typography variant="body2">
                OBJECT-TYPE: {displayValue(diagnostic.values?.objectType)} {'\u00b7'} SKEY:{' '}
                {displayValue(diagnostic.values?.skey)}
              </Typography>
              <Typography variant="body2">
                Ends: {[1, 2, 3, 4].map((index) => displayValue(
                  diagnostic.values?.[`end${index}` as keyof typeof diagnostic.values]
                )).join(' \u00b7 ')}
              </Typography>
              <Typography variant="caption" color="text.secondary">
                Pattern: {diagnostic.matchedPatternId ?? '\u2014'}
              </Typography>
            </Stack>
          )}
          <Divider />
          <Typography variant="body2">
            Matching local candidates: {diagnostic.learnedCandidates.length}
          </Typography>
        </>
      )}
    </Stack>
  );
};

export default QuickFillKeyLibraryMatchTester;
